import addQuarters from 'date-fns/addQuarters'
import startOfQuarter from 'date-fns/startOfQuarter'
import addMonths from 'date-fns/addMonths'
import startOfMonth from 'date-fns/startOfMonth'
import endOfMonth from 'date-fns/endOfMonth'
import endOfQuarter from 'date-fns/endOfQuarter'
import addYears from 'date-fns/addYears'
import subDays from 'date-fns/subDays'

import { SelectOption } from 'Components/Inputs/StyledSelect'
import { PolicyPeriodDuration } from 'Utils/types/benefitProgramPolicy'
import { displayMonthAndYear, formatDateForServer, monthName, parseServerDate } from './dateUtils'

type SelectOptionsConfig = {
  start?: boolean
  annualResetMonth?: number
  // Creating a program may start it in the current period; moving a live policy may not.
  includeCurrentPeriod?: boolean
  after?: Date
}

export const getSelectOptions = (
  policyDuration: PolicyPeriodDuration,
  { start = true, annualResetMonth, includeCurrentPeriod = false, after }: SelectOptionsConfig = {}
) => {
  const options = optionsForDuration(policyDuration, start, annualResetMonth, includeCurrentPeriod)

  return after ? options.filter((option) => parseServerDate(option.value) > after) : options
}

const optionsForDuration = (
  policyDuration: PolicyPeriodDuration,
  start: boolean,
  annualResetMonth?: number,
  includeCurrentPeriod = false
): SelectOption[] => {
  switch (policyDuration) {
    case PolicyPeriodDuration.Monthly:
    case PolicyPeriodDuration.OneTime:
      return getMonthlySelectOptions(start, includeCurrentPeriod)
    case PolicyPeriodDuration.Quarterly:
      return getQuarterlySelectOptions(start, includeCurrentPeriod)
    case PolicyPeriodDuration.Yearly:
      return getYearlySelectOptions(annualResetMonth, start, includeCurrentPeriod)
    default:
      return []
  }
}

export const getAnnualResetMonthOptions = (): SelectOption[] =>
  Array.from({ length: 12 }, (_, index) => ({
    value: String(index + 1),
    label: monthName(index + 1)
  }))

// Monthly: Next 16 months
const getMonthlySelectOptions = (start = true, includeCurrentPeriod = false) => {
  const monthlyOptions: SelectOption[] = []
  let nextDate = new Date()

  // If it's not start, include current month
  if (!start) {
    monthlyOptions.push({
      value: formatDateForServer(endOfMonth(nextDate)),
      label: 'End of this month'
    })
  }

  if (start && includeCurrentPeriod) {
    monthlyOptions.push({
      value: formatDateForServer(startOfMonth(nextDate)),
      label: displayMonthAndYear(nextDate)
    })
  }

  for (let i = 0; i < 16; i++) {
    const nextMonth = addMonths(nextDate, 1)

    nextDate = start ? startOfMonth(nextMonth) : endOfMonth(nextMonth)

    monthlyOptions.push({
      value: formatDateForServer(nextDate),
      label: displayMonthAndYear(nextDate)
    })
  }

  return monthlyOptions
}

// Quarterly: Next 6 quarters
const getQuarterlySelectOptions = (start = true, includeCurrentPeriod = false) => {
  const quarterlyOptions: SelectOption[] = []
  let nextQuarter = new Date()
  const label = (date: Date) => displayMonthAndYear(start ? date : endOfQuarter(date))

  if (includeCurrentPeriod || !start) {
    const current = start ? startOfQuarter(nextQuarter) : endOfQuarter(nextQuarter)
    quarterlyOptions.push({
      value: formatDateForServer(current),
      label: start ? displayMonthAndYear(current) : 'End of this quarter'
    })
  }

  for (let i = 0; i < 6; i++) {
    nextQuarter = startOfQuarter(addQuarters(nextQuarter, 1))

    quarterlyOptions.push({
      value: formatDateForServer(start ? nextQuarter : endOfQuarter(nextQuarter)),
      label: label(nextQuarter)
    })
  }

  return quarterlyOptions
}

// Annual: the next occurrences of the 1st of the reset month
const getYearlySelectOptions = (
  annualResetMonth?: number,
  start = true,
  includeCurrentPeriod = false
): SelectOption[] => {
  if (!annualResetMonth) return []

  const today = new Date()
  const month = annualResetMonth - 1
  const currentPeriodStart = new Date(
    today.getMonth() >= month ? today.getFullYear() : today.getFullYear() - 1,
    month,
    1
  )
  const offsets = start && !includeCurrentPeriod ? [1, 2] : [0, 1, 2]

  return offsets.map((offset) => {
    const periodStart = addYears(currentPeriodStart, offset)
    const date = start ? periodStart : subDays(addYears(periodStart, 1), 1)
    return { value: formatDateForServer(date), label: displayMonthAndYear(date) }
  })
}
